/** Validate Attack / DeclareAttack / ResolveAttack actions (rule.md §6, §7). */
import { areAdjacent } from "../data/board";
import type {
  AttackAction,
  DeclareAttackAction,
  ResolveAttackAction,
} from "../types/action";
import { invalid, type ValidationResult, valid } from "../types/common";
import type { AugmentedWarState } from "../types/game-state";
import { opponentOf } from "../utils/state";
import { validateActionBudget, validateTurn } from "./shared";

/** Shared attacker-side checks for atomic Attack and live DeclareAttack. */
function validateAttackerSide(
  state: AugmentedWarState,
  action: AttackAction | DeclareAttackAction,
): ValidationResult {
  const turn = validateTurn(state, action.playerId);
  if (!turn.valid) return turn;
  const budget = validateActionBudget(state, "attack");
  if (!budget.valid) return budget;

  const { from, target, playerId, attackerElement } = action;
  const attacker = state.board[from];
  if (attacker?.owner !== playerId) {
    return invalid("No own unit at the attacker cell", { from });
  }
  const defender = state.board[target];
  if (!defender) {
    return invalid("No unit at the target cell", { target });
  }
  if (defender.owner === playerId) {
    return invalid("Cannot attack your own unit", { target });
  }
  if (!areAdjacent(from, target)) {
    return invalid("Target must be adjacent", { from, target });
  }
  // Element cooldown — shared attack+defense charges (rule.md §7.3).
  if (state.players[playerId].spentElements.includes(attackerElement)) {
    return invalid("Attacker element is on cooldown this turn", {
      attackerElement,
    });
  }
  return valid;
}

export function validateAttack(
  state: AugmentedWarState,
  action: AttackAction,
): ValidationResult {
  const attacker = validateAttackerSide(state, action);
  if (!attacker.valid) return attacker;
  const defenderId = opponentOf(action.playerId);
  if (
    state.players[defenderId].spentElements.includes(action.defenderElement)
  ) {
    return invalid("Defender element is on cooldown this turn", {
      defenderElement: action.defenderElement,
    });
  }
  return valid;
}

/** Live step 1 — the attacker commits (defender's element is not known yet). */
export function validateDeclareAttack(
  state: AugmentedWarState,
  action: DeclareAttackAction,
): ValidationResult {
  return validateAttackerSide(state, action);
}

/**
 * Live step 2 — the defender responds out-of-turn to the pending attack, so we
 * check ownership against the pending attacker, NOT the current player.
 */
export function validateResolveAttack(
  state: AugmentedWarState,
  action: ResolveAttackAction,
): ValidationResult {
  const pending = state.pendingAttack;
  if (!pending) {
    return invalid("No attack is awaiting resolution");
  }
  const defenderId = opponentOf(pending.attacker);
  if (action.playerId !== defenderId) {
    return invalid("Only the defender may resolve the pending attack", {
      defenderId,
      playerId: action.playerId,
    });
  }
  if (
    state.players[defenderId].spentElements.includes(action.defenderElement)
  ) {
    return invalid("Defender element is on cooldown this turn", {
      defenderElement: action.defenderElement,
    });
  }
  return valid;
}
